import { Button, Card, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Iconify from '../../../../../../reusable-components/iconify';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormDataDivider from '../../../FormFields/FormDataDivider';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormNumberField from '../../../FormFields/FormNumberField';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import FormVendorField from '../../../FormFields/FormVendorField';
import { formCardStyle } from '../../../TransitionStyles';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { PAYROLL } from '../../../formQuestionsEnum';
import DietarySection from './DietarySection';
import HousekeepingSection from './HousekeepingSection';
import TherapySection from './TherapySection';
import { downloadPayrollTemplate } from './utils';

PayrollForm.propTypes = {
  permissions: PropTypes.object,
};

export default function PayrollForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);
  const fieldIds = Object.values(PAYROLL).map((item) => item?.id);

  const showForm = fieldIds.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Payroll
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {PAYROLL.PAYROLL_VENDOR.id in permissions && (
            <>
              <Grid item xs={12}>
                <FieldContainer>
                  <FormTitle fieldAttributes={{ questionTitle: 'Payroll vendor', fieldType: 'title' }} />
                  <FormVendorField
                    fieldName={PAYROLL.PAYROLL_VENDOR.fieldName}
                    fieldAttributes={{ isEditable: permissions[PAYROLL.PAYROLL_VENDOR.id] }}
                    invoiceFileTypeId={FILE_TYPES.PAYROLL_VENDOR_INVOICE}
                    contractFileTypeId={FILE_TYPES.PAYROLL_VENDOR_CONTRACT}
                  />
                </FieldContainer>
              </Grid>
              <FormDataDivider />
            </>
          )}
          {[PAYROLL.NUMBER_OF_TIME_CLOCKS.id, PAYROLL.TIME_AND_ATTENDANCE_SOFTWARE_VENDOR.id].some(
            (id) => id in permissions
          ) && (
            <>
              {PAYROLL.NUMBER_OF_TIME_CLOCKS.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'How many time clocks does the facility have?' }} />
                  <FormNumberField
                    fieldName={PAYROLL.NUMBER_OF_TIME_CLOCKS.fieldName}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                    fieldAttributes={{
                      isEditable: permissions[PAYROLL.NUMBER_OF_TIME_CLOCKS.id],
                    }}
                  />
                </Grid>
              )}
              {PAYROLL.EMPLOYEE_PAYROLL_FORM.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Employee Payroll Form', fieldType: 'file' }} />
                    <Button
                      color="secondary"
                      variant="outlined"
                      size="small"
                      onClick={downloadPayrollTemplate}
                      startIcon={<Iconify icon="file-icons:microsoft-excel" />}
                      sx={{ mt: 1 }}
                      disabled={!permissions[PAYROLL.EMPLOYEE_PAYROLL_FORM.id]}
                    >
                      Download template
                    </Button>
                    <FormFileDrop
                      fieldName={PAYROLL.EMPLOYEE_PAYROLL_FORM.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[PAYROLL.EMPLOYEE_PAYROLL_FORM.id],
                        fileTypeId: FILE_TYPES.EMPLOYEE_PAYROLL_FORM,
                      }}
                    />
                  </FieldContainer>
                </Grid>
              )}
              {PAYROLL.TIME_AND_ATTENDANCE_SOFTWARE_VENDOR.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle
                      fieldAttributes={{ questionTitle: 'Time and attendance software vendor', fieldType: 'title' }}
                    />
                    <FormVendorField
                      fieldName={PAYROLL.TIME_AND_ATTENDANCE_SOFTWARE_VENDOR.fieldName}
                      fieldAttributes={{ isEditable: permissions[PAYROLL.TIME_AND_ATTENDANCE_SOFTWARE_VENDOR.id] }}
                      invoiceFileTypeId={FILE_TYPES.TIME_AND_ATTENDANCE_SOFTWARE_VENDOR_INVOICE}
                      contractFileTypeId={FILE_TYPES.TIME_AND_ATTENDANCE_SOFTWARE_VENDOR_CONTRACT}
                    />
                  </FieldContainer>
                </Grid>
              )}
              <FormDataDivider />
            </>
          )}
          {[
            PAYROLL.CURRENT_EMPLOYEES_HANDBOOK.id,
            PAYROLL.COLLECTIVE_BARGAINING_CONTRACTS.id,
            PAYROLL.TIME_CARD_REPORT.id,
            PAYROLL.PAYROLL_REPORT.id,
          ].some((id) => id in permissions) && (
            <>
              {PAYROLL.CURRENT_EMPLOYEES_HANDBOOK.id in permissions && (
                <Grid item xs={12} sm={6}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Current employee handbook', fieldType: 'file' }} />
                    <FormFileDrop
                      fieldName={PAYROLL.CURRENT_EMPLOYEES_HANDBOOK.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[PAYROLL.CURRENT_EMPLOYEES_HANDBOOK.id],
                        fileTypeId: FILE_TYPES.EMPLOYEE_HANDBOOK,
                      }}
                    />
                  </FieldContainer>
                </Grid>
              )}
              {PAYROLL.COLLECTIVE_BARGAINING_CONTRACTS.id in permissions && (
                <Grid item xs={12} sm={6}>
                  <FieldContainer>
                    <FormTitle
                      fieldAttributes={{ questionTitle: 'Collective bargaining contracts', fieldType: 'file' }}
                    />
                    <FormFileDrop
                      fieldName={PAYROLL.COLLECTIVE_BARGAINING_CONTRACTS.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[PAYROLL.COLLECTIVE_BARGAINING_CONTRACTS.id],
                        fileTypeId: FILE_TYPES.COLLECTIVE_BARGAINING_CONTRACTS,
                      }}
                    />
                  </FieldContainer>
                </Grid>
              )}
              {PAYROLL.TIME_CARD_REPORT.id in permissions && (
                <Grid item xs={12} sm={6}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Most recent time card report', fieldType: 'file' }} />
                    <FormFileDrop
                      fieldName={PAYROLL.TIME_CARD_REPORT.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[PAYROLL.TIME_CARD_REPORT.id],
                        fileTypeId: FILE_TYPES.TIME_CARD_REPORT,
                      }}
                    />
                  </FieldContainer>
                </Grid>
              )}
              {PAYROLL.PAYROLL_REPORT.id in permissions && (
                <Grid item xs={12} sm={6}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Most recent payroll report', fieldType: 'file' }} />
                    <FormFileDrop
                      fieldName={PAYROLL.PAYROLL_REPORT.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[PAYROLL.PAYROLL_REPORT.id],
                        fileTypeId: FILE_TYPES.PAYROLL_REPORT,
                      }}
                    />
                  </FieldContainer>
                </Grid>
              )}
              <FormDataDivider />
            </>
          )}
          {[
            PAYROLL.HOLIDAY_PAY_RULE.id,
            PAYROLL.SECOND_SHIFT_PAY_RULE.id,
            PAYROLL.THIRD_SHIFT_PAY_RULE.id,
            PAYROLL.WEEKEND_SHIFT_PAY_RULE.id,
            PAYROLL.OVERTIME_PAY_RULE.id,
            PAYROLL.PTO_PAY_RULE.id,
            PAYROLL.FINAL_BALANCE.id,
          ].some((id) => id in permissions) && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="text.secondary">
                  Pay rules
                </Typography>
              </Grid>
              {PAYROLL.HOLIDAY_PAY_RULE.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'Holiday' }} />
                  <FormTextField
                    fieldName={PAYROLL.HOLIDAY_PAY_RULE.fieldName}
                    fieldAttributes={{ isEditable: permissions[PAYROLL.HOLIDAY_PAY_RULE.id] }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              {PAYROLL.SECOND_SHIFT_PAY_RULE.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'Second shift' }} />
                  <FormTextField
                    fieldName={PAYROLL.SECOND_SHIFT_PAY_RULE.fieldName}
                    fieldAttributes={{ isEditable: permissions[PAYROLL.SECOND_SHIFT_PAY_RULE.id] }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              {PAYROLL.THIRD_SHIFT_PAY_RULE.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'Third shift' }} />
                  <FormTextField
                    fieldName={PAYROLL.THIRD_SHIFT_PAY_RULE.fieldName}
                    fieldAttributes={{ isEditable: permissions[PAYROLL.THIRD_SHIFT_PAY_RULE.id] }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              {PAYROLL.WEEKEND_SHIFT_PAY_RULE.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'Weekend shift' }} />
                  <FormTextField
                    fieldName={PAYROLL.WEEKEND_SHIFT_PAY_RULE.fieldName}
                    fieldAttributes={{ isEditable: permissions[PAYROLL.WEEKEND_SHIFT_PAY_RULE.id] }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              {PAYROLL.OVERTIME_PAY_RULE.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'Overtime' }} />
                  <FormTextField
                    fieldName={PAYROLL.OVERTIME_PAY_RULE.fieldName}
                    fieldAttributes={{ isEditable: permissions[PAYROLL.OVERTIME_PAY_RULE.id] }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              {PAYROLL.PTO_PAY_RULE.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'PTO' }} />
                  <FormTextField
                    fieldName={PAYROLL.PTO_PAY_RULE.fieldName}
                    fieldAttributes={{ isEditable: permissions[PAYROLL.PTO_PAY_RULE.id] }}
                    focusedField={focusedField}
                    setFocusedField={setFocusedField}
                  />
                </Grid>
              )}
              {PAYROLL.FINAL_BALANCE.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Final balance', fieldType: 'file' }} />
                    <FormFileDrop
                      fieldName={PAYROLL.FINAL_BALANCE.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[PAYROLL.FINAL_BALANCE.id],
                        fileTypeId: FILE_TYPES.PAY_RULES_FINAL_BALANCE,
                      }}
                    />
                  </FieldContainer>
                </Grid>
              )}
              <FormDataDivider />
            </>
          )}
          <HousekeepingSection permissions={permissions} />
          <DietarySection permissions={permissions} />
          <TherapySection permissions={permissions} />
        </Grid>
      </Card>
    )
  );
}
